import React from 'react';

import PropTypes from 'prop-types';

import Card from '@/components/common/Card';
import clientImage from '@/components/common/icons/client-circle.svg';
import detailsImage from '@/components/common/icons/client-details.svg';
import contactImage from '@/components/common/icons/contact-circle.svg';
import Spinner from '@/components/common/Spinner';
import AddressIcon from '@/svg-icons/address';
import ClientIcon from '@/svg-icons/client';

const SectionTitle = ({ icon, children, isFirst }) => (
  <h5 className={`flex items-center mb-2 text-[0.75em] !font-semibold leading-[1.1] text-[#393939] ${isFirst ? 'mt-0' : '!mt-[1.5em]'}`}>
    <div className="w-[24px] h-[24px] mr-[0.5em] flex items-center justify-center flex-shrink-0">
      {typeof icon === 'string' ? (
        <img src={icon} alt="" className="h-[24px] w-auto" />
      ) : (
        React.cloneElement(icon, { className: 'h-[24px] w-auto' })
      )}
    </div>
    {children}
  </h5>
);

SectionTitle.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isFirst: PropTypes.bool,
};

SectionTitle.defaultProps = {
  isFirst: false,
};

const Field = ({ label, value, className }) => (
  <div className={`flex flex-col mt-[1.25em] px-[3.46em] first:mt-0 ${className || ''}`}>
    <p className="m-0 text-[0.75em] font-bold uppercase text-[#393939]">{label}</p>
    <p className="text-[0.875em] font-medium text-[#919191] mt-[0.4em] mb-0 leading-[1.4] overflow-hidden text-ellipsis whitespace-nowrap">
      {value || '-----'}
    </p>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  className: PropTypes.string,
};

Field.defaultProps = {
  value: null,
  className: null,
};

const FieldRow = ({ children }) => (
  <div className="flex flex-row justify-between mt-[1.25em] px-[3.46em] first:mt-0">
    {children}
  </div>
);

FieldRow.propTypes = {
  children: PropTypes.node.isRequired,
};

const FieldCol = ({ label, value, width }) => (
  <div className={`flex flex-col mt-0 px-0 ${width || 'w-[calc(50%-5px)]'}`}>
    <p className="m-0 text-[0.75em] font-bold uppercase text-[#393939]">{label}</p>
    <p className="text-[0.875em] font-medium text-[#919191] mt-[0.4em] mb-0 leading-[1.4] overflow-hidden text-ellipsis whitespace-nowrap">
      {value || '-----'}
    </p>
  </div>
);

FieldCol.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  width: PropTypes.string,
};

FieldCol.defaultProps = {
  value: null,
  width: 'w-[calc(50%-5px)]',
};

const ClientDetails = ({ busy, client, intl }) => {
  if (busy) {
    return (
      <div className="flex flex-col p-[1.5em_1em_0]">
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  if (!client) {
    return null;
  }

  const isPJ = client?.client_type === 'pj';

  return (
    <div className="flex flex-col p-[1.5em_1em_0]">
      <Card className="fade-in !p-[1.75em_1.625em]">
        <h4 className="flex items-center mb-[1.4em] pb-[1.4em] border-b border-[#eee] text-[1em] !font-bold leading-[1.3] text-[#393939]">
          <div className="mr-[0.5em] w-[28px] h-[28px] flex items-center justify-center">
            <img src={detailsImage} alt={intl.formatMessage({ id: 'DETAILS' })} className="max-h-full max-w-full" />
          </div>
          {intl.formatMessage({ id: 'DETAILS' })}
        </h4>

        <SectionTitle icon={clientImage} isFirst>
          {intl.formatMessage({ id: 'CUSTOMER' })}
        </SectionTitle>

        <Field label={intl.formatMessage({ id: 'CUSTOMER_TYPE' })} value={isPJ ? intl.formatMessage({ id: 'LEGAL_PERSON' }) : intl.formatMessage({ id: 'NATURAL_PERSON' })} />

        <Field label={isPJ ? intl.formatMessage({ id: 'FANTASY_NAME' }) : intl.formatMessage({ id: 'NAME' })} value={client?.name} />

        <FieldRow>
          {isPJ ? (
            <>
              <FieldCol label={intl.formatMessage({ id: 'CNPJ' })} value={client?.document} />
              <FieldCol label={intl.formatMessage({ id: 'COMPANY_NAME' })} value={client?.company_name} />
            </>
          ) : (
            <FieldCol label={intl.formatMessage({ id: 'CPF' })} value={client?.document} />
          )}
        </FieldRow>

        <FieldRow>
          <FieldCol label={intl.formatMessage({ id: 'USER' })} value={client?.username} />
          <FieldCol label={intl.formatMessage({ id: 'EMAIL_ADDRESS' })} value={client?.contact?.email} />
        </FieldRow>

        {isPJ && (
          <>
            <SectionTitle icon={<ClientIcon />}>
              {intl.formatMessage({ id: 'RESPONSIBLE' })}
            </SectionTitle>

            <Field label={intl.formatMessage({ id: 'NAME' })} value={client?.responsible?.name} />

            <FieldRow>
              <FieldCol label={intl.formatMessage({ id: 'CPF' })} value={client?.responsible?.document} width="w-[calc(33.33%-6.67px)]" />
              <FieldCol label={intl.formatMessage({ id: 'PHONE' })} value={client?.responsible?.phone} width="w-[calc(33.33%-6.67px)]" />
              <FieldCol label={intl.formatMessage({ id: 'CELLPHONE' })} value={client?.responsible?.cellphone} width="w-[calc(33.33%-6.67px)]" />
            </FieldRow>

            <Field label={intl.formatMessage({ id: 'EMAIL' })} value={client?.responsible?.email} />
          </>
        )}

        <SectionTitle icon={<AddressIcon />}>
          {intl.formatMessage({ id: 'CUSTOMER_ADDRESS' })}
        </SectionTitle>

        <FieldRow>
          <FieldCol label={intl.formatMessage({ id: 'CEP' })} value={client?.address?.zipcode} />
        </FieldRow>

        <FieldRow>
          <FieldCol label={intl.formatMessage({ id: 'STREET' })} value={client?.address?.street} />
          <FieldCol label={intl.formatMessage({ id: 'NUMBER' })} value={client?.address?.number} width="w-[calc(25%-7.5px)]" />
          <FieldCol label={intl.formatMessage({ id: 'COMPLEMENT' })} value={client?.address?.complement} width="w-[calc(25%-7.5px)]" />
        </FieldRow>

        <FieldRow>
          <FieldCol label={intl.formatMessage({ id: 'NEIGHBORHOOD' })} value={client?.address?.neighborhood} width="w-[calc(41.66%-5.83px)]" />
          <FieldCol label={intl.formatMessage({ id: 'CITY' })} value={client?.address?.city} width="w-[calc(41.66%-5.83px)]" />
          <FieldCol label={intl.formatMessage({ id: 'STATE' })} value={client?.address?.state} width="w-[calc(16.66%-8.33px)]" />
        </FieldRow>

        <SectionTitle icon={contactImage}>
          {intl.formatMessage({ id: 'CONTACT' })}
        </SectionTitle>

        <FieldRow>
          <FieldCol label={intl.formatMessage({ id: 'PHONE' })} value={client?.contact?.phone} />
          <FieldCol label={intl.formatMessage({ id: 'CELLPHONE' })} value={client?.contact?.cellphone} />
        </FieldRow>
      </Card>
    </div>
  );
};

ClientDetails.propTypes = {
  busy: PropTypes.bool.isRequired,
  client: PropTypes.object,
  intl: PropTypes.object.isRequired,
};

ClientDetails.defaultProps = {
  client: null,
};

export default ClientDetails;
